import React, { useState, useEffect } from 'react';
import { FileText, Brain, CheckCircle, AlertCircle, ChevronDown, ChevronRight, ArrowLeft, MapPin, Droplets, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getProjectDetails, ProjectDetails, VWBAAnalysis, ValidationFlag, PreviousYearData } from '../services/api';

interface ProjectDetailsPageProps {
  projectId: string;
  onBack: () => void;
}

// Helper functions for formatting (matching ProjectsPage.tsx)
const formatStatus = (status?: string): string => {
  const statusMap: Record<string, string> = {
    clean: 'Clean',
    stage1: 'Stage 1 Flagged',
    stage2: 'Stage 2 Flagged',
  };
  return statusMap[status || ''] || status || '-';
};

const formatCategory = (category?: string): string => {
  const categoryMap: Record<string, string> = {
    reforestation: 'Reforestation',
    wetland: 'Wetland Restoration',
    efficiency: 'Water Efficiency',
    infrastructure: 'Infrastructure',
  };
  return categoryMap[category || ''] || category || '-';
};

const ProjectDetailsPage: React.FC<ProjectDetailsPageProps> = ({ projectId, onBack }) => {
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');

  useEffect(() => {
    loadProjectDetails();
  }, [projectId]);

  const loadProjectDetails = async () => {
    try {
      setLoading(true);
      const projectData = await getProjectDetails(projectId);
      setProject(projectData);
    } catch (err) {
      setError('Failed to load project details');
      console.error('Error loading project details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isSectionCollapsed = (sectionId: string) => {
    return collapsedSections[sectionId] || false;
  };

  if (loading) {
    return (
      <div className="h-full w-full px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <FileText className="w-12 h-12 mx-auto mb-2" />
            <p className="text-lg font-medium">Error Loading Project</p>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadProjectDetails}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="h-full w-full px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Project not found</p>
          <button
            onClick={onBack}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full px-4 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{project.name}</h2>
            <p className="text-gray-600">{project.description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('structured')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              viewMode === 'structured'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Structured Data
          </button>
          <button
            onClick={() => setViewMode('raw')}
            className={`px-3 py-1 rounded-md text-sm font-medium ${
              viewMode === 'raw'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Raw Response
          </button>
        </div>
      </div>

      {/* Project Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Project Information
          </h3>
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span>Created: {formatDate(project.createdAt)}</span>
            {project.updatedAt !== project.createdAt && (
              <span>Updated: {formatDate(project.updatedAt)}</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="font-medium text-gray-700">Project Name:</span>
            <p className="text-sm text-gray-600 mt-1">{project.name}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Description:</span>
            <p className="text-sm text-gray-600 mt-1">{project.description || 'No description provided'}</p>
          </div>
        </div>
      </div>

      {/* Validation Status - Only show if validation data exists */}
      {project.status && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Validation Status
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Badge */}
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              project.status === 'clean' ? 'bg-green-100 text-green-800' :
              project.status === 'stage1' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
            }`}>
              {formatStatus(project.status)}
            </span>
            {/* Type Badge */}
            {project.type && (
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                project.type === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'
              }`}>
                {project.type === 'new' ? 'New Project' : 'Historic Project'}
              </span>
            )}
            {/* Category Badge */}
            {project.category && (
              <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {formatCategory(project.category)}
              </span>
            )}
          </div>
          {/* Clean Status Message */}
          {project.status === 'clean' && (
            <div className="mt-4 p-4 rounded-lg bg-green-50 border-l-4 border-green-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-800">Validation Status: Clean</h4>
              </div>
              <p className="text-sm text-green-700 mt-1">
                This project has passed all automated validation checks and meets VWBA 2.0 requirements. No manual review required.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Project Details Grid - Only show if validation details exist */}
      {(project.basin || project.volumetricWaterBenefit || project.costShare || project.quantificationMethod) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {project.basin && (
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="flex items-center text-xs text-gray-500 uppercase mb-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  Basin
                </dt>
                <dd className="text-sm font-medium text-gray-900">{project.basin}</dd>
              </div>
            )}
            {project.volumetricWaterBenefit && (
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="flex items-center text-xs text-gray-500 uppercase mb-1">
                  <Droplets className="w-4 h-4 mr-1" />
                  Volumetric Water Benefit
                </dt>
                <dd className="text-sm font-medium text-gray-900">{project.volumetricWaterBenefit}</dd>
              </div>
            )}
            {project.costShare && (
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-xs text-gray-500 uppercase mb-1">Cost-Share</dt>
                <dd className="text-sm font-medium text-gray-900">{project.costShare}</dd>
              </div>
            )}
            {project.quantificationMethod && (
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-xs text-gray-500 uppercase mb-1">Quantification Method</dt>
                <dd className="text-sm font-medium text-gray-900">{project.quantificationMethod}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Validation Flags Section */}
      {(() => {
        let parsedFlags: ValidationFlag[] = [];
        try {
          parsedFlags = project.flags ? JSON.parse(project.flags) : [];
        } catch {
          console.error('Failed to parse validation flags');
          return null;
        }
        if (parsedFlags.length === 0) return null;
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <button
              onClick={() => toggleSection('validation-flags')}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className={`w-5 h-5 mr-2 ${
                  project.status === 'stage2' ? 'text-red-600' : 'text-amber-600'
                }`} />
                Validation Flags ({parsedFlags.length})
              </h3>
              {isSectionCollapsed('validation-flags') ? (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            {!isSectionCollapsed('validation-flags') && (
              <div className="mt-4 space-y-3">
                {parsedFlags.map((flag, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${
                      project.status === 'stage2' ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'
                    }`}
                  >
                    <h4 className="font-semibold text-gray-900">{flag.type}</h4>
                    <p className="text-sm text-gray-600 mt-1">{flag.description}</p>
                    <dl className="mt-2 text-sm space-y-1">
                      <div><dt className="inline font-semibold">Affected Field:</dt> <dd className="inline">{flag.field}</dd></div>
                      {flag.systemValue && <div><dt className="inline font-semibold">System Value:</dt> <dd className="inline">{flag.systemValue}</dd></div>}
                      {flag.factSheetValue && <div><dt className="inline font-semibold">Fact Sheet Value:</dt> <dd className="inline">{flag.factSheetValue}</dd></div>}
                      {flag.expectedValue && <div><dt className="inline font-semibold">Expected:</dt> <dd className="inline">{flag.expectedValue}</dd></div>}
                      {flag.requirement && <div><dt className="inline font-semibold">Requirement:</dt> <dd className="inline">{flag.requirement}</dd></div>}
                      {flag.riskLevel && <div><dt className="inline font-semibold">Risk Level:</dt> <dd className="inline">{flag.riskLevel}</dd></div>}
                      {flag.previousValue && <div><dt className="inline font-semibold">Previous Year:</dt> <dd className="inline">{flag.previousValue}</dd></div>}
                      {flag.change && <div><dt className="inline font-semibold">Change:</dt> <dd className="inline">{flag.change}</dd></div>}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Previous Year Comparison */}
      {(() => {
        let previousYear: PreviousYearData | null = null;
        try {
          previousYear = project.previousYearData
            ? JSON.parse(project.previousYearData)
            : null;
        } catch {
          console.error('Failed to parse previous year data');
          return null;
        }
        if (!previousYear) return null;
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Previous Year Comparison</h3>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <dt className="text-xs text-gray-500 uppercase mb-1">Previous Volumetric Water Benefit</dt>
                <dd className="text-sm font-medium text-gray-900">{previousYear.volumetricWaterBenefit}</dd>
              </div>
              {previousYear.costShare && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <dt className="text-xs text-gray-500 uppercase mb-1">Previous Cost-Share</dt>
                  <dd className="text-sm font-medium text-gray-900">{previousYear.costShare}</dd>
                </div>
              )}
            </dl>
          </div>
        );
      })()}

      {/* Fact Sheets and Analyses */}
      {project.factSheets && project.factSheets.length > 0 ? (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Fact Sheets & Analyses</h3>
          {project.factSheets.map((factSheet) => (
            <div key={factSheet.id} className="bg-white rounded-lg border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-900 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  {factSheet.originalName}
                </h4>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    factSheet.status === 'PROCESSED' ? 'bg-green-100 text-green-800' :
                    factSheet.status === 'PROCESSING' ? 'bg-yellow-100 text-yellow-800' :
                    factSheet.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {factSheet.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatDate(factSheet.createdAt)}
                  </span>
                </div>
              </div>

              {/* Analyses for this fact sheet */}
              {factSheet.analyses && factSheet.analyses.length > 0 ? (
                <div className="space-y-4">
                  {factSheet.analyses.map((analysis) => (
                    <div key={analysis.id} className="bg-gray-50 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium text-gray-800 flex items-center">
                          <Brain className="w-4 h-4 mr-2" />
                          Analysis Results
                        </h5>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            analysis.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            analysis.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                            analysis.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {analysis.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(analysis.createdAt)}
                          </span>
                        </div>
                      </div>

                      {analysis.status === 'COMPLETED' && analysis.results && (
                        <div className="space-y-4">
                          {viewMode === 'structured' && (
                            <div className="space-y-6">
                              {/* Parse and display the VWBA analysis results */}
                              {(() => {
                                try {
                                  const vwbaAnalysis: VWBAAnalysis = JSON.parse(analysis.results);
                                  return (
                                    <>
                                      {/* Project Information */}
                                      <div className="bg-gray-50 rounded-lg p-4">
                                        <button
                                          onClick={() => toggleSection(`${analysis.id}-project-info`)}
                                          className="w-full flex items-center justify-between text-left"
                                        >
                                          <h6 className="font-semibold text-gray-800 flex items-center">
                                            <FileText className="w-5 h-5 mr-2" />
                                            Project Information
                                          </h6>
                                          {isSectionCollapsed(`${analysis.id}-project-info`) ? (
                                            <ChevronRight className="w-5 h-5 text-gray-500" />
                                          ) : (
                                            <ChevronDown className="w-5 h-5 text-gray-500" />
                                          )}
                                        </button>
                                        {!isSectionCollapsed(`${analysis.id}-project-info`) && (
                                          <div className="space-y-2">
                                            <div>
                                              <span className="font-medium text-gray-700">Project Name:</span>
                                              <p className="text-sm text-gray-600 mt-1">{vwbaAnalysis.projectName}</p>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-700">Description:</span>
                                              <p className="text-sm text-gray-600 mt-1">{vwbaAnalysis.projectDescription}</p>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                Overall Confidence: {vwbaAnalysis.confidence}%
                                              </span>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Recommended Methodology */}
                                      <div className="bg-green-50 rounded-lg p-4">
                                        <button
                                          onClick={() => toggleSection(`${analysis.id}-recommended-methodology`)}
                                          className="w-full flex items-center justify-between text-left"
                                        >
                                          <h6 className="font-semibold text-gray-800 flex items-center">
                                            <Brain className="w-5 h-5 mr-2 text-green-600" />
                                            Recommended Methodology
                                          </h6>
                                          {isSectionCollapsed(`${analysis.id}-recommended-methodology`) ? (
                                            <ChevronRight className="w-5 h-5 text-gray-500" />
                                          ) : (
                                            <ChevronDown className="w-5 h-5 text-gray-500" />
                                          )}
                                        </button>
                                        {!isSectionCollapsed(`${analysis.id}-recommended-methodology`) && (
                                          <div className="space-y-3">
                                            <div>
                                              <span className="font-medium text-gray-700">Methodology:</span>
                                              <p className="text-sm text-gray-600 mt-1">{vwbaAnalysis.recommendedMethodology.name}</p>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-700">Description:</span>
                                              <p className="text-sm text-gray-600 mt-1">{vwbaAnalysis.recommendedMethodology.description}</p>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-700">Confidence:</span>
                                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {vwbaAnalysis.recommendedMethodology.confidence}%
                                              </span>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-700">Reasoning:</span>
                                              <p className="text-sm text-gray-600 mt-1">{vwbaAnalysis.recommendedMethodology.reasoning}</p>
                                            </div>
                                            
                                            {/* Methodology Inputs */}
                                            <div>
                                              <span className="font-medium text-gray-700">Required Inputs:</span>
                                              <div className="mt-2 space-y-2">
                                                {vwbaAnalysis.recommendedMethodology.inputs.map((input, index) => (
                                                  <div key={index} className="bg-white rounded p-3 border">
                                                    <div className="flex items-center justify-between">
                                                      <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                                      <div className="flex items-center space-x-2">
                                                        {input.available ? (
                                                          <CheckCircle className="w-4 h-4 text-green-500" />
                                                        ) : (
                                                          <AlertCircle className="w-4 h-4 text-red-500" />
                                                        )}
                                                        <span className={`text-xs px-2 py-1 rounded ${
                                                          input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                          {input.required ? 'Required' : 'Optional'}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                                                    {input.value && (
                                                      <p className="text-sm text-gray-600 mt-1">
                                                        <span className="font-medium">Value:</span> {input.value} {input.unit && `(${input.unit})`}
                                                      </p>
                                                    )}
                                                    {input.source && (
                                                      <p className="text-xs text-gray-400 mt-1">
                                                        <span className="font-medium">Source:</span> {input.source}
                                                      </p>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>

                                            {/* Calculation Steps */}
                                            <div>
                                              <span className="font-medium text-gray-700">Calculation Steps:</span>
                                              <ol className="mt-2 space-y-1">
                                                {vwbaAnalysis.recommendedMethodology.calculationSteps.map((step, index) => (
                                                  <li key={index} className="text-sm text-gray-600 flex">
                                                    <span className="font-medium mr-2">{index + 1}.</span>
                                                    <span>{step}</span>
                                                  </li>
                                                ))}
                                              </ol>
                                            </div>

                                            {/* References */}
                                            <div>
                                              <span className="font-medium text-gray-700">References:</span>
                                              <ul className="mt-2 space-y-1">
                                                {vwbaAnalysis.recommendedMethodology.references.map((ref, index) => (
                                                  <li key={index} className="text-sm text-gray-600 flex">
                                                    <span className="mr-2">•</span>
                                                    <span>{ref}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Alternative Methodologies */}
                                      {vwbaAnalysis.alternativeMethodologies && vwbaAnalysis.alternativeMethodologies.length > 0 && (
                                        <div className="bg-yellow-50 rounded-lg p-4">
                                          <button
                                            onClick={() => toggleSection(`${analysis.id}-alternative-methodologies`)}
                                            className="w-full flex items-center justify-between text-left"
                                          >
                                            <h6 className="font-semibold text-gray-800 flex items-center">
                                              <AlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
                                              Alternative Methodologies
                                            </h6>
                                            {isSectionCollapsed(`${analysis.id}-alternative-methodologies`) ? (
                                              <ChevronRight className="w-5 h-5 text-gray-500" />
                                            ) : (
                                              <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                          </button>
                                          {!isSectionCollapsed(`${analysis.id}-alternative-methodologies`) && (
                                            <div className="space-y-4">
                                              {vwbaAnalysis.alternativeMethodologies.map((methodology, index) => (
                                                <div key={index} className="bg-white rounded p-3 border">
                                                  <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium text-gray-700">{methodology.name}</span>
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                      {methodology.confidence}% confidence
                                                    </span>
                                                  </div>
                                                  <p className="text-sm text-gray-600 mb-2">{methodology.description}</p>
                                                  <p className="text-xs text-gray-500">{methodology.reasoning}</p>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Available and Missing Inputs */}
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {/* Available Inputs */}
                                        <div className="bg-green-50 rounded-lg p-4">
                                          <button
                                            onClick={() => toggleSection(`${analysis.id}-available-inputs`)}
                                            className="w-full flex items-center justify-between text-left"
                                          >
                                            <h6 className="font-semibold text-gray-800 flex items-center">
                                              <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                                              Available Inputs ({vwbaAnalysis.availableInputs.length})
                                            </h6>
                                            {isSectionCollapsed(`${analysis.id}-available-inputs`) ? (
                                              <ChevronRight className="w-5 h-5 text-gray-500" />
                                            ) : (
                                              <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                          </button>
                                          {!isSectionCollapsed(`${analysis.id}-available-inputs`) && (
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                              {vwbaAnalysis.availableInputs.map((input, index) => (
                                                <div key={index} className="bg-white rounded p-3 border">
                                                  <div className="flex items-center justify-between">
                                                    <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                                  </div>
                                                  <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                                                  {input.value && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                      <span className="font-medium">Value:</span> {input.value} {input.unit && `(${input.unit})`}
                                                    </p>
                                                  )}
                                                  {input.source && (
                                                    <p className="text-xs text-gray-400 mt-1">
                                                      <span className="font-medium">Source:</span> {input.source}
                                                    </p>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>

                                        {/* Missing Inputs */}
                                        <div className="bg-red-50 rounded-lg p-4">
                                          <button
                                            onClick={() => toggleSection(`${analysis.id}-missing-inputs`)}
                                            className="w-full flex items-center justify-between text-left"
                                          >
                                            <h6 className="font-semibold text-gray-800 flex items-center">
                                              <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                                              Missing Inputs ({vwbaAnalysis.missingInputs.length})
                                            </h6>
                                            {isSectionCollapsed(`${analysis.id}-missing-inputs`) ? (
                                              <ChevronRight className="w-5 h-5 text-gray-500" />
                                            ) : (
                                              <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                          </button>
                                          {!isSectionCollapsed(`${analysis.id}-missing-inputs`) && (
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                              {vwbaAnalysis.missingInputs.map((input, index) => (
                                                <div key={index} className="bg-white rounded p-3 border">
                                                  <div className="flex items-center justify-between">
                                                    <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                                    <div className="flex items-center space-x-2">
                                                      <AlertCircle className="w-4 h-4 text-red-500" />
                                                      <span className={`text-xs px-2 py-1 rounded ${
                                                        input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                                      }`}>
                                                        {input.required ? 'Required' : 'Optional'}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                                                  {input.unit && (
                                                    <p className="text-xs text-gray-400 mt-1">
                                                      <span className="font-medium">Unit:</span> {input.unit}
                                                    </p>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {/* Processing Notes */}
                                      {vwbaAnalysis.processingNotes && vwbaAnalysis.processingNotes.length > 0 && (
                                        <div className="bg-purple-50 rounded-lg p-4">
                                          <button
                                            onClick={() => toggleSection(`${analysis.id}-processing-notes`)}
                                            className="w-full flex items-center justify-between text-left"
                                          >
                                            <h6 className="font-semibold text-gray-800 flex items-center">
                                              <Brain className="w-5 h-5 mr-2 text-purple-600" />
                                              Processing Notes
                                            </h6>
                                            {isSectionCollapsed(`${analysis.id}-processing-notes`) ? (
                                              <ChevronRight className="w-5 h-5 text-gray-500" />
                                            ) : (
                                              <ChevronDown className="w-5 h-5 text-gray-500" />
                                            )}
                                          </button>
                                          {!isSectionCollapsed(`${analysis.id}-processing-notes`) && (
                                            <div className="space-y-2">
                                              {vwbaAnalysis.processingNotes.map((note, index) => (
                                                <div key={index} className="bg-white rounded p-3 border">
                                                  <p className="text-sm text-gray-600">{note}</p>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  );
                                } catch (error) {
                                  return (
                                    <div className="bg-red-50 rounded-lg p-4">
                                      <p className="text-sm text-red-600">Error parsing analysis results</p>
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          )}

                          {viewMode === 'raw' && analysis.rawResponse && (
                            <div className="space-y-4">
                              <h6 className="font-medium text-gray-700 mb-2">Raw Gemini Response</h6>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                                  {analysis.rawResponse}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {analysis.status === 'ERROR' && (
                        <div className="bg-red-50 rounded-lg p-4">
                          <p className="text-sm text-red-600">Analysis failed</p>
                        </div>
                      )}

                      {analysis.status === 'IN_PROGRESS' && (
                        <div className="bg-yellow-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm text-yellow-600">Analysis in progress...</span>
                          </div>
                        </div>
                      )}

                      {analysis.status === 'PENDING' && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600">Analysis pending</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">No analyses available for this fact sheet</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Fact Sheets</h3>
            <p className="text-gray-600">This project doesn't have any uploaded fact sheets yet.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetailsPage;





